import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import LISSCallType from "../../types/core/LISSCallType";
import LISSIntegration from "../../types/core/LISSIntegration";
import SIFIntegration from "../../types/core/SIFIntegration";
import Datastore from "../../communication/Datastore";

import store, { IAppState } from "../../state/Store";
import { LISSResolutionRule } from "../../types/core/LISSResolutionRule";
import IntegrationErrorLog from "../../types/core/IntegrationErrorLog";
import PageSpecification from "../../types/system/PageSpecification";
import LISSTimetableStructure from "../../types/core/LISSTimetableStructure";

// Define the type for our state store entry
type IntegrationState = {
    lissData: LISSIntegration[];
    lissCallData: LISSCallType[];
    lissCallsId: number;
    lissCalls: number[];
    lissResolutions: LISSResolutionRule[];
    lissStructures: LISSTimetableStructure[];
    lissStatus: string;
    sifData: SIFIntegration[];
    sifStatus: string;
    virtualSpec: PageSpecification;
    internalSpec: PageSpecification;
    logType: string;
    logId: number;
    logAllData: Record<number, IntegrationErrorLog[]>;
    logPageData: IntegrationErrorLog[];
    logStatus: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: IntegrationState = {
    lissData: [],
    lissCallData: [],
    lissCallsId: 0,
    lissCalls: [],
    lissResolutions: [],
    lissStructures: [],
    lissStatus: "idle",
    sifData: [],
    sifStatus: "idle",
    logType: "",
    logId: 0,
    logAllData: {},
    logPageData: [],
    logStatus: "idle",
    virtualSpec: {
        page: 0,
        size: 20,
        total: 0,
    } as PageSpecification,
    internalSpec: {
        page: -1,
        size: 200,
        total: 0,
    } as PageSpecification,
    error: null,
};

// Create a slice to hold all our reducers
const integrationSlice = createSlice({
    name: "integration",
    initialState: initialState,
    reducers: {
        updateSIFIntegrationDetails: (
            state,
            action: PayloadAction<SIFIntegration>,
        ) => {
            const update = new SIFIntegration(action.payload);
            const index = state.sifData.indexOf(
                state.sifData.find(item => item.id === update.id),
            );

            if (index >= 0) state.sifData.splice(index, 1, update);
            else state.sifData.push(update);
        },
        deleteSIFIntegrationDetails: (
            state,
            action: PayloadAction<SIFIntegration>,
        ) => {
            const update = new SIFIntegration(action.payload);
            const index = state.sifData.indexOf(
                state.sifData.find(item => item.id === update.id),
            );

            if (index >= 0) state.sifData.splice(index, 1);
        },
        updateLISSIntegrationDetails: (
            state,
            action: PayloadAction<LISSIntegration>,
        ) => {
            const update = new LISSIntegration(action.payload);
            const index = state.lissData.indexOf(
                state.lissData.find(item => item.id === update.id),
            );

            if (index >= 0) state.lissData.splice(index, 1, update);
            else state.lissData.push(update);
        },
        updateLISSResolutionDetails: (
            state,
            action: PayloadAction<LISSResolutionRule[]>,
        ) => {
            state.lissResolutions = action.payload.map(
                resolution => new LISSResolutionRule(resolution),
            );
        },
        deleteLISSIntegrationDetails: (
            state,
            action: PayloadAction<LISSIntegration>,
        ) => {
            const update = new LISSIntegration(action.payload);
            const index = state.lissData.indexOf(
                state.lissData.find(item => item.id === update.id),
            );

            if (index >= 0) state.lissData.splice(index, 1);
        },
        resetLISSCallState: state => {
            state.lissCalls = [];
            state.lissCallsId = 0;
        },
        setLISSCallState: (
            state,
            action: PayloadAction<{ call: number; enabled: boolean }>,
        ) => {
            if (action.payload.enabled) {
                if (!state.lissCalls.includes(action.payload.call)) {
                    state.lissCalls = [...state.lissCalls, action.payload.call];
                }
            } else {
                state.lissCalls = state.lissCalls.filter(
                    c => c !== action.payload.call,
                );
            }
        },
        clearIntegrationErrorLog: state => {
            state.logType = "";
            state.logId = 0;
            state.logAllData = {};
            state.logPageData = [];
            state.virtualSpec = {
                page: 0,
                size: 20,
                total: 0,
            } as PageSpecification;
            state.internalSpec = {
                page: -1,
                size: 200,
                total: 0,
            } as PageSpecification;
        },
        setVirtualPageAndSize: (
            state,
            action: PayloadAction<PageSpecification>,
        ) => {
            state.virtualSpec.page = action.payload.page;
            state.virtualSpec.size = action.payload.size;

            const internalPageIndex = Math.floor(
                (state.virtualSpec.page * state.virtualSpec.size) /
                    state.internalSpec.size,
            );
            const internalPage = state.logAllData[internalPageIndex];
            const translatedPageStart =
                state.virtualSpec.page * state.virtualSpec.size -
                internalPageIndex * state.internalSpec.size;
            const translatedPageEnd =
                translatedPageStart + state.virtualSpec.size;

            state.logPageData =
                internalPage?.slice(
                    Math.min(translatedPageStart, internalPage.length),
                    Math.min(translatedPageEnd, internalPage.length),
                ) ?? [];

            state.virtualSpec.waiting =
                state.virtualSpec.loading && state.logPageData.length === 0;

            while (state.logPageData.length < state.virtualSpec.size)
                state.logPageData.push(new IntegrationErrorLog());
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const allData = {};

                if (rawData?.payload?.integration?.logAllData)
                    for (let entry in rawData?.payload?.integration?.logAllData)
                        allData[entry] =
                            rawData?.payload?.integration?.logAllData[
                                entry
                            ].map(item => new IntegrationErrorLog(item));

                return {
                    lissData: rawData?.payload?.integration?.lissData ?? [],
                    lissCallData:
                        rawData?.payload?.integration?.lissCallData ?? [],
                    lissCalls: [],
                    lissResolutions: [],
                    lissStructures: [],
                    lissCallsId: 0,
                    lissStatus:
                        !rawData?.payload?.integration?.lissData ||
                        !rawData?.payload?.integration?.lissData.length
                            ? "idle"
                            : rawData?.payload?.integration?.lissStatus,
                    sifData: rawData?.payload?.integration?.sifData ?? [],
                    sifStatus:
                        !rawData?.payload?.integration?.sifData ||
                        !rawData?.payload?.integration?.sifData.length
                            ? "idle"
                            : rawData?.payload?.integration?.sifStatus,
                    logType: rawData?.payload?.integration?.logType ?? "",
                    logId: rawData?.payload?.integration?.logId ?? 0,
                    logAllData: allData,
                    logPageData:
                        rawData?.payload?.integration?.logPageData?.map(
                            item => new IntegrationErrorLog(item),
                        ) ?? [],
                    logStatus: "idle",
                    virtualSpec:
                        rawData?.payload?.integration?.virtualSpec ??
                        initialState.virtualSpec,
                    internalSpec:
                        rawData?.payload?.integration?.internalSpec ??
                        initialState.internalSpec,
                    error: null,
                };
            })
            .addCase(fetchLISSIntegrations.pending, (state, action) => {
                state.lissStatus = "loading";
            })
            .addCase(fetchLISSIntegrations.fulfilled, (state, action) => {
                state.lissStatus = "succeeded";
                // Add any fetched posts to the array
                state.lissCallData = state.lissCallData.concat(
                    action.payload[0],
                );
                state.lissData = state.lissData.concat(
                    action.payload[1].map(
                        record => new LISSIntegration(record),
                    ),
                );
            })
            .addCase(fetchLISSIntegrations.rejected, (state, action) => {
                state.lissStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchLISSAdditionalConfig.pending, (state, action) => {
                state.lissStatus = "loading";
            })
            .addCase(fetchLISSAdditionalConfig.fulfilled, (state, action) => {
                state.lissStatus = "succeeded";
                // Add any fetched posts to the array
                state.lissCallsId = action.payload.lissCallsId;
                state.lissCalls = action.payload.calls;
                state.lissResolutions = action.payload.resolutions;
                state.lissStructures = action.payload.structures.map(
                    structure => new LISSTimetableStructure(structure),
                );
            })
            .addCase(fetchLISSAdditionalConfig.rejected, (state, action) => {
                state.lissStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchSIFIntegrations.pending, (state, action) => {
                state.sifStatus = "loading";
            })
            .addCase(fetchSIFIntegrations.fulfilled, (state, action) => {
                state.sifStatus = "succeeded";
                // Add any fetched posts to the array
                state.sifData = state.sifData.concat(
                    action.payload.map(record => new SIFIntegration(record)),
                );
            })
            .addCase(fetchSIFIntegrations.rejected, (state, action) => {
                state.sifStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(updateLISSIntegration.pending, (state, action) => {
                state.lissStatus = "loading";
            })
            .addCase(updateLISSIntegration.fulfilled, (state, action) => {
                state.lissStatus = "succeeded";
                state.lissData = state.lissData.map(record =>
                    record.id === action.payload.id
                        ? new LISSIntegration(action.payload)
                        : record,
                );
            })
            .addCase(updateLISSIntegration.rejected, (state, action) => {
                state.lissStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(updateSIFIntegration.pending, (state, action) => {
                state.sifStatus = "loading";
            })
            .addCase(updateSIFIntegration.fulfilled, (state, action) => {
                state.sifStatus = "succeeded";
                state.sifData = state.sifData.map(record =>
                    record.id === action.payload.id
                        ? new SIFIntegration(action.payload)
                        : record,
                );
            })
            .addCase(updateSIFIntegration.rejected, (state, action) => {
                state.sifStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(initialiseLog.pending, (state, action) => {
                state.logStatus = "loading";
                state.virtualSpec.loading = true;
                state.internalSpec.loading = true;
            })
            .addCase(initialiseLog.fulfilled, (state, action) => {
                state.logStatus = "succeeded";
                state.logType = action.payload[0];
                state.logId = action.payload[1];
                state.virtualSpec.total = action.payload[2];
                state.internalSpec.total = action.payload[2];
                processLogPage(state, 0, action.payload[3]);
            })
            .addCase(initialiseLog.rejected, (state, action) => {
                state.logStatus = "failed";
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.error = action.error.message;
            })
            .addCase(loadLog.pending, (state, action) => {
                state.logStatus = "loading";
                state.virtualSpec.loading = true;
                state.internalSpec.loading = true;
            })
            .addCase(loadLog.fulfilled, (state, action) => {
                state.logStatus = "succeeded";
                processLogPage(state, action.payload[0], action.payload[1]);
            })
            .addCase(loadLog.rejected, (state, action) => {
                state.logStatus = "failed";
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { setLISSCallState, resetLISSCallState } =
    integrationSlice.actions;

const {
    updateSIFIntegrationDetails,
    deleteSIFIntegrationDetails,
    updateLISSIntegrationDetails,
    updateLISSResolutionDetails,
    deleteLISSIntegrationDetails,
    setVirtualPageAndSize,
    clearIntegrationErrorLog,
} = integrationSlice.actions;

const processLogPage = (
    state: IntegrationState,
    page: number,
    data: IntegrationErrorLog[],
) => {
    state.virtualSpec.loading = false;
    state.internalSpec.loading = false;
    state.logAllData[page] = data.map(
        record => new IntegrationErrorLog(record),
    );

    const internalPageIndex = Math.floor(
        (state.virtualSpec.page * state.virtualSpec.size) /
            state.internalSpec.size,
    );
    const internalPage = state.logAllData[internalPageIndex];
    const translatedPageStart =
        state.virtualSpec.page * state.virtualSpec.size -
        internalPageIndex * state.internalSpec.size;
    const translatedPageEnd = translatedPageStart + state.virtualSpec.size;

    state.logPageData =
        internalPage?.slice(
            Math.min(translatedPageStart, internalPage.length),
            Math.min(translatedPageEnd, internalPage.length),
        ) ?? [];

    state.virtualSpec.waiting =
        state.virtualSpec.loading && state.logPageData.length === 0;

    while (state.logPageData.length < state.virtualSpec.size)
        state.logPageData.push(new IntegrationErrorLog({}));
};
const evaluatePrivateData = createAsyncThunk(
    "integration/evaluatePrivateData",
    async (specification: PageSpecification, thunkAPI): Promise<void> => {
        const { integrations } = store.getState().db;
        const requiredInternalPage = Math.max(
            Math.ceil(
                ((specification.page + 1) * specification.size - 1) /
                    integrations.internalSpec.size,
            ) - 1,
            0,
        );

        if (integrations.logStatus !== "loading") {
            if (!integrations.logAllData[requiredInternalPage]) {
                await thunkAPI.dispatch(
                    loadLog({
                        type: integrations.logType,
                        id: integrations.logId,
                        page: requiredInternalPage,
                    }),
                );
            } else {
                const direction =
                    specification.page > integrations.virtualSpec.page
                        ? 1
                        : specification.page < integrations.virtualSpec.page
                        ? -1
                        : 0;

                if (direction !== 0) {
                    const pageGap =
                        direction > 0
                            ? (integrations.internalSpec.size -
                                  (specification.page * specification.size -
                                      requiredInternalPage *
                                          integrations.internalSpec.size)) /
                              specification.size
                            : (specification.page * specification.size -
                                  requiredInternalPage *
                                      integrations.internalSpec.size) /
                              specification.size;

                    if (pageGap < 4) {
                        if (
                            direction < 0 &&
                            requiredInternalPage > 0 &&
                            !integrations.logAllData[requiredInternalPage - 1]
                        ) {
                            await thunkAPI.dispatch(
                                loadLog({
                                    type: integrations.logType,
                                    id: integrations.logId,
                                    page: requiredInternalPage - 1,
                                }),
                            );
                        } else if (
                            direction > 0 &&
                            !integrations.logAllData[requiredInternalPage + 1]
                        ) {
                            await thunkAPI.dispatch(
                                loadLog({
                                    type: integrations.logType,
                                    id: integrations.logId,
                                    page: requiredInternalPage + 1,
                                }),
                            );
                        }
                    }
                }
            }
        }
    },
);
export const getIntegrationLogPage = createAsyncThunk(
    "integration/getIntegrationLogPage",
    async (_, thunkAPI): Promise<void> => {
        const { integrations } = store.getState().db;
        thunkAPI.dispatch(evaluatePrivateData(integrations.virtualSpec));
    },
);
export const setIntegrationLogPageAndSize = createAsyncThunk(
    "integration/setIntegrationLogPageAndSize",
    async (specification: PageSpecification, thunkAPI) => {
        thunkAPI.dispatch(evaluatePrivateData(specification));
        thunkAPI.dispatch(setVirtualPageAndSize(specification));
    },
);

// This is a thunk, they are used to load the state
export const fetchLISSIntegrations = createAsyncThunk(
    "integration/fetchLISSIntegrations",
    async () => {
        const response = await Promise.all([
            Datastore.getType<LISSCallType[]>("api/LISS/Config"),
            Datastore.getType<LISSIntegration[]>("api/LISS/Consumptions"),
        ]);
        return response;
    },
    {
        condition: () =>
            (store.getState() as IAppState).db.integrations.lissStatus ===
            "idle",
    },
);
export const initialiseLISSIntegrations = createAsyncThunk(
    "integration/initialiseLISSIntegrations",
    async (_, thunkAPI) => {
        const { integrations } = store.getState().db;

        if (integrations.lissStatus === "idle")
            await thunkAPI.dispatch(fetchLISSIntegrations());
    },
);
export const fetchLISSAdditionalConfig = createAsyncThunk(
    "integration/fetchLISSAdditionalConfig",
    async (consumptionId: number) => {
        const { integrations } = (store.getState() as IAppState).db;
        const [calls, resolutions, structures] = await Promise.all([
            consumptionId
                ? await Datastore.getType<number[]>(
                      `api/LISS/Consumptions/${consumptionId}/ConfigIds`,
                  )
                : integrations.lissCallData.map(record => record.id),
            consumptionId
                ? await Datastore.getType<LISSResolutionRule[]>(
                      `api/LISS/Consumptions/${consumptionId}/Resolutions`,
                  )
                : [],
            consumptionId
                ? await Datastore.getType<LISSTimetableStructure[]>(
                      `api/LISS/Consumptions/${consumptionId}/TimetableStructure`,
                  )
                : [],
        ]);
        return {
            lissCallsId: consumptionId,
            calls: calls,
            resolutions: resolutions,
            structures: structures,
        };
    },
    {
        condition: (consumptionId: number) => {
            const { integrations } = (store.getState() as IAppState).db;
            return (
                integrations.lissStatus !== "loading" &&
                integrations.lissCallsId !== consumptionId
            );
        },
    },
);
export const updateLISSIntegration = createAsyncThunk(
    "integration/updateLISSIntegration",
    async (data: LISSIntegration, thunkAPI): Promise<LISSIntegration> => {
        const { integrations } = store.getState().db;
        const response = await (data.id > 0
            ? Datastore.putType<LISSIntegration[]>("api/LISS/Consumptions", [
                  data,
              ])
            : Datastore.postType<LISSIntegration[]>("api/LISS/Consumptions", [
                  data,
              ]));
        await Datastore.put(
            `api/LISS/Consumptions/${response[0].id}/ConfigIds`,
            integrations.lissCalls,
        );
        return response[0];
    },
);
export const updateLISSIntegrationResolutions = createAsyncThunk(
    "integration/updateLISSIntegrationResolutions",
    async (
        config: {
            integration: LISSIntegration;
            resolutions: LISSResolutionRule[];
        },
        thunkAPI,
    ): Promise<void> => {
        await Datastore.putType<LISSIntegration[]>(
            `api/LiSS/Consumptions/${config.integration.id}/Resolutions`,
            config.resolutions,
        );
        thunkAPI.dispatch(updateLISSResolutionDetails(config.resolutions));
    },
);
export const syncLISSIntegration = createAsyncThunk(
    "integration/syncLISSIntegration",
    async (data: LISSIntegration, thunkAPI): Promise<void> =>
        Datastore.get(`api/LISS/Consumptions/${data.id}/Sync`),
    {
        condition: (data: LISSIntegration) => data.id > 0,
    },
);
export const validateLISSIntegration = createAsyncThunk(
    "integration/validateLISSIntegration",
    async (data: LISSIntegration, thunkAPI): Promise<void> =>
        Datastore.get(`api/LISS/Consumptions/${data.id}/Validate`),
    {
        condition: (data: LISSIntegration) => data.id > 0,
    },
);
export const deleteLISSIntegration = createAsyncThunk(
    "integration/deleteLISSIntegration",
    async (data: LISSIntegration, thunkAPI): Promise<void> => {
        await Promise.all([
            Datastore.delete(`api/LISS/Consumptions/${data.id}`),
        ]);
        thunkAPI.dispatch(deleteLISSIntegrationDetails(data));
    },
);
export const deleteLISSIntegrationFiles = createAsyncThunk(
    "integration/deleteLISSIntegrationFiles",
    async (data: LISSIntegration, thunkAPI): Promise<void> => {
        await Promise.all([
            Datastore.get(`api/LISS/Consumptions/${data.id}/RemoveFiles`),
        ]);
        data.tT_FileID = null;
        thunkAPI.dispatch(updateLISSIntegrationDetails(data));
    },
);

export const fetchSIFIntegrations = createAsyncThunk(
    "integration/fetchSIFIntegrations",
    async () => {
        const response = await Datastore.getType<SIFIntegration[]>(
            "api/SIF/Consumptions",
        );
        return response;
    },
    {
        condition: () =>
            (store.getState() as IAppState).db.integrations.sifStatus ===
            "idle",
    },
);
export const initialiseSIFIntegrations = createAsyncThunk(
    "integration/initialiseSIFIntegrations",
    async (_, thunkAPI) => {
        const { integrations } = store.getState().db;

        if (integrations.sifStatus === "idle")
            await thunkAPI.dispatch(fetchSIFIntegrations());
    },
);
export const updateSIFIntegration = createAsyncThunk(
    "integration/updateSIFIntegration",
    async (data: SIFIntegration, thunkAPI): Promise<SIFIntegration> => {
        const response = await (data.id > 0
            ? Datastore.putType<SIFIntegration[]>("api/SIF/Consumptions", [
                  data,
              ])
            : Datastore.postType<SIFIntegration[]>("api/SIF/Consumptions", [
                  data,
              ]));
        return response[0];
    },
);
export const syncSIFIntegration = createAsyncThunk(
    "integration/syncSIFIntegration",
    async (data: SIFIntegration, thunkAPI): Promise<void> =>
        Datastore.get(`api/SIF/Consumptions/${data.id}/Sync`),
);
export const deleteSIFIntegration = createAsyncThunk(
    "integration/deleteSIFIntegration",
    async (data: SIFIntegration, thunkAPI): Promise<void> => {
        await Promise.all([
            Datastore.post("api/SIF/Consumptions/Delete", [data.id]),
        ]);
        thunkAPI.dispatch(deleteSIFIntegrationDetails(data));
    },
);
export const deleteSIFIntegrationFiles = createAsyncThunk(
    "integration/deleteSIFIntegrationFiles",
    async (data: SIFIntegration, thunkAPI): Promise<void> => {
        await Promise.all([
            Datastore.get(`api/SIF/Consumptions/${data.id}/RemoveFiles`),
        ]);
        data.tT_FileID = null;
        thunkAPI.dispatch(updateSIFIntegrationDetails(data));
    },
);

export const initialiseLog = createAsyncThunk(
    "integration/initialiseLog",
    async (config: { type: string; id: number }, thunkAPI) => {
        thunkAPI.dispatch(clearIntegrationErrorLog());
        const { integrations } = store.getState().db;
        return Promise.all([
            config.type,
            config.id,
            Datastore.getType<number>(
                `api/${config.type}/Consumptions/${config.id}/ErrorLog/Count`,
            ),
            Datastore.getType<IntegrationErrorLog[]>(
                `api/${config.type}/Consumptions/${config.id}/ErrorLog/Page/0/Size/${integrations.internalSpec.size}`,
            ),
        ]);
    },
    {
        condition: (config: { type: string; id: number }) => config.id > 0,
    },
);
export const reinitialiseLog = createAsyncThunk(
    "integration/reinitialiseLog",
    async (config: { type: string; id: number }, thunkAPI) => {
        thunkAPI.dispatch(clearIntegrationErrorLog());
        await thunkAPI.dispatch(initialiseLog(config)).unwrap();
    },
    {
        condition: (config: { type: string; id: number }) => config.id > 0,
    },
);
const loadLog = createAsyncThunk(
    "integration/loadLog",
    async (config: { type: string; id: number; page: number }, thunkAPI) => {
        const { integrations } = store.getState().db;
        return Promise.all([
            config.page,
            Datastore.getType<IntegrationErrorLog[]>(
                `api/${config.type}/Consumptions/${config.id}/ErrorLog/Page/${config.page}/Size/${integrations.internalSpec.size}`,
            ),
        ]);
    },
    {
        condition: (config: { type: string; id: number; page: number }) =>
            config.id > 0,
    },
);

// We have to export the reducer as this is added to the state store
export default integrationSlice.reducer;
